import React, { useState, memo, useEffect } from "react";
import "./SeasonalityPdf.css";
import ReactApexChart from "react-apexcharts";
import ArrowUp from "@mui/icons-material/ArrowUpward";
import ArrowDown from "@mui/icons-material/ArrowDownward";
import {
  Table,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
} from "@mui/material";

const stringConstant = {
  min : "Min. Temp (f)",
  max : "Max. Temp (f)"
}

const applyToArray = (func, array) => func.apply(Math, array);
const SeasonalityPdf = memo((props) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [orderBy, setOrderBy] = useState("profileName");
  const [orderDirection, setOrderDirection] = useState(true);
  const [priceAnalysis, setPriceAnalysis] = useState([]);
  const [apexValue, setApexValue] = useState({
    series: [],
    options: {
      chart: {
        height: 350,
        type: "line",
        toolbar: {
          show: false,
          offsetX: 0,
          offsetY: 0,
          tools: {
            download: false,
            selection: true,
            zoom: false,
            zoomin: false,
            zoomout: false,
            pan: false,
            reset: false,
            customIcons: [],
          },
        },
      },
      stroke: {
        curve: "smooth",
        width: 2,
      },
      fill: {
        type: "solid",
        opacity: 0.1,
      },
      labels: [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep ",
        "Oct",
        "Nov",
        "Dec",
      ],
      markers: {
        size: 0,
      },
      yaxis: [
        {
          labels: {
            formatter: (value) => {
              return value + " F";
            },
          },
        },
      ],
      colors: ["#000000", "#5b9f48", "#ab54a0", "#d51f29"],
      tooltip: {
        shared: true,
        intersect: false,
        y: {
          formatter: function (y, x) {
            if (typeof y !== "undefined") {
              let caption = x.seriesIndex > 1 ? " in" : " °";
              return y.toFixed(0) + caption;
            }
            return y;
          },
        },
      },
    },
  });

  //get seasonality analysis table data from props
  useEffect(() => {
    if (props && props.data) {
      if (props.value === "1") {
        let sampleData = [];
        let color = [];
        let showPrecipitation = false;
        let precValues = [];
        let tempValues = [];
        props.data.map((data) => {
          if (data.weatherdata === "precipitation") {
            showPrecipitation = true;
            for (var i = 0; i < Object.keys(data).length; i++) {
              if (
                Object.values(data)[i] !== "precipitation" &&
                Object.values(data)[i] !== null
              ) {
                precValues.push(Object.values(data)[i]);
              }
            }
          } else {
            for (var i = 0; i < Object.keys(data).length; i++) {
              if (
                Object.values(data)[i] !== "max. temp (f)" &&
                Object.values(data)[i] !== "min. temp (f)" &&
                Object.values(data)[i] !== null
              ) {
                tempValues.push(Object.values(data)[i]);
              }
            }
          }

          sampleData.push({
            name:
                data.weatherdata === "precipitation"
                  ? data.weatherdata.charAt(0).toUpperCase() + data.weatherdata.slice(1) + " (in)"
                  : data.weatherdata.includes('min') ? stringConstant.min : stringConstant.max ,
            type: data.weatherdata === "precipitation" ? "area" : "line",
            data: [
              data.january ? data.january : null,
              data.february ? data.february : null,
              data.march ? data.march : null,
              data.april ? data.april : null,
              data.may ? data.may : null,
              data.june ? data.june : null,
              data.july ? data.july : null,
              data.august ? data.august : null,
              data.september ? data.september : null,
              data.october ? data.october : null,
              data.november ? data.november : null,
              data.december ? data.december : null,
            ],
          });
          color.push(
            data.weatherdata === "precipitation"
              ? "#000000"
              : data.weatherdata === "max. temp (f)"
              ? "#5b9f48"
              : "#d51f29"
          );
        });
        let preciMinMax = [
          applyToArray(Math.min, precValues),
          applyToArray(Math.max, precValues),
        ];
        let tempMinMax = [
          applyToArray(Math.min, tempValues),
          applyToArray(Math.max, tempValues),
        ];
        setApexValue({
          ...apexValue,
          series: sampleData,
          options: {
            colors: color,
            noData: {
              text: sampleData.length === 0 ? "" : "No Data Found",
              style: { color: "red", fontSize: "12px" },
            },
            axisBorder: {
              show: sampleData.length === 0 ? false : true,
            },
            yaxis: [
              {
                seriesName: 'Min. Temp (f)',
                show: true,
                title: {
                  text: "Avg. Temperature (F)",
                },
                labels: {
                  formatter: (value) => {
                    return parseInt(value);
                  },
                },
              },
              {
                seriesName: 'Max. Temp (f)',               
                show: false,
              },
             {
                show: showPrecipitation,
                opposite: showPrecipitation,
                title: {
                  text: "Avg. Precipitation (in)",
                },
                seriesName: 'Precipitation',
              },
            ],
          },
        });
      } else {
        let priceArray = [];
        const avg_priceResult = props.data.reduce(function (results, org) {
          (results[org.profilename] = results[org.profilename] || []).push(org);
          return results;
        }, {});
        priceArray.push(avg_priceResult);
        let filteredPriceArray = Array.from(
          new Set(priceArray.map(JSON.stringify)),
          JSON.parse
        );
        setPriceAnalysis(filteredPriceArray);
      }
    }
  }, [props && props.data]);

  //move to next page of the records
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  //change the display rows per page in record
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value));
  };
  const handleInfiniteOnLoad = () => {
    setPage(page + 1);
  };

  return (
    <div className="season-tab">
      {props.value === "1" ? (
        <div>
          <div className="table-legend">
            <span>Showing the average weather data of 5 years by month</span>
          </div>
          <div id="chart" className="chart-phbs chart-box">
            <div className="chart-main">
              <ReactApexChart
                options={apexValue && apexValue.options && apexValue.options}
                series={apexValue && apexValue.series && apexValue.series}
                type="line"
                height={320}
              />
            </div>
          </div>
          <div></div>
        </div>
      ) : (
        <div className="season-table">
          <Table className="table-design">
            <TableHead>
              <TableRow>
                <TableCell>
                  <span
                    className="hover-hand"
                    onClick={() => {
                      setOrderDirection(!orderDirection);
                      setOrderBy("profileName");
                    }}
                  >
                    Campgrounds {orderDirection ? <ArrowDown /> : <ArrowUp />}
                  </span>
                </TableCell>
                <TableCell></TableCell>
                <TableCell>Jan</TableCell>
                <TableCell>Feb</TableCell>
                <TableCell>Mar</TableCell>
                <TableCell>Apr</TableCell>
                <TableCell>May</TableCell>
                <TableCell>Jun</TableCell>
                <TableCell>Jul</TableCell>
                <TableCell>Aug</TableCell>
                <TableCell>Sep</TableCell>
                <TableCell>Oct</TableCell>
                <TableCell>Nov</TableCell>
                <TableCell>Dec</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {priceAnalysis && priceAnalysis.length > 0 ? (
                priceAnalysis.map((x) => {
                  let dt = Object.values(x);
                  return dt.map((sampleData) => {
                    return sampleData.map((data, index) => {
                      return (
                        <React.Fragment>
                          <TableRow key={data.profilename}>
                            {index === 0 ? (
                              <TableCell
                                rowSpan={sampleData.length}
                                className="profile-name"
                              >
                                {data.profilename}
                              </TableCell>
                            ) : (
                              ""
                            )}
                            <TableCell>
                              {data.status === "avg. temp (f)"
                                ? "Avg. Temp (F)"
                                : data.status === "avgprecipitation"
                                ? "Avg. Precipitation (in)"
                                : "Status"}
                            </TableCell>
                            <TableCell
                              className={
                                data.january === "Open"
                                  ? "green-text"
                                  : data.january === "Closed"
                                  ? "red-text"
                                  : "black-text"
                              }
                            >
                              {data.status === "avg. temp (f)" ||
                              data.status === "avgprecipitation"
                                ? data.january
                                  ? data.january
                                  : "-"
                                : data.january
                                ? data.january
                                : "-"}
                            </TableCell>
                            <TableCell
                              className={
                                data.february === "Open"
                                  ? "green-text"
                                  : data.february === "Closed"
                                  ? "red-text"
                                  : "black-text"
                              }
                            >
                              {data.status === "avg. temp (f)" ||
                              data.status === "avgprecipitation"
                                ? data.february
                                  ? data.february
                                  : "-"
                                : data.february
                                ? data.february
                                : "-"}
                            </TableCell>
                            <TableCell
                              className={
                                data.march === "Open"
                                  ? "green-text"
                                  : data.march === "Closed"
                                  ? "red-text"
                                  : "black-text"
                              }
                            >
                              {data.status === "avg. temp (f)" ||
                              data.status === "avgprecipitation"
                                ? data.march
                                  ? data.march
                                  : "-"
                                : data.march
                                ? data.march
                                : "-"}
                            </TableCell>
                            <TableCell
                              className={
                                data.april === "Open"
                                  ? "green-text"
                                  : data.april === "Closed"
                                  ? "red-text"
                                  : "black-text"
                              }
                            >
                              {data.status === "avg. temp (f)" ||
                              data.status === "avgprecipitation"
                                ? data.april
                                  ? data.april
                                  : "-"
                                : data.april
                                ? data.april
                                : "-"}
                            </TableCell>
                            <TableCell
                              className={
                                data.may === "Open"
                                  ? "green-text"
                                  : data.may === "Closed"
                                  ? "red-text"
                                  : "black-text"
                              }
                            >
                              {data.status === "avg. temp (f)" ||
                              data.status === "avgprecipitation"
                                ? data.may
                                  ? data.may
                                  : "-"
                                : data.may
                                ? data.may
                                : "-"}
                            </TableCell>
                            <TableCell
                              className={
                                data.june === "Open"
                                  ? "green-text"
                                  : data.june === "Closed"
                                  ? "red-text"
                                  : "black-text"
                              }
                            >
                              {data.status === "avg. temp (f)" ||
                              data.status === "avgprecipitation"
                                ? data.june
                                  ? data.june
                                  : "-"
                                : data.june
                                ? data.june
                                : "-"}
                            </TableCell>
                            <TableCell
                              className={
                                data.july === "Open"
                                  ? "green-text"
                                  : data.july === "Closed"
                                  ? "red-text"
                                  : "black-text"
                              }
                            >
                              {data.status === "avg. temp (f)" ||
                              data.status === "avgprecipitation"
                                ? data.july
                                  ? data.july
                                  : "-"
                                : data.july
                                ? data.july
                                : "-"}
                            </TableCell>
                            <TableCell
                              className={
                                data.august === "Open"
                                  ? "green-text"
                                  : data.august === "Closed"
                                  ? "red-text"
                                  : "black-text"
                              }
                            >
                              {data.status === "avg. temp (f)" ||
                              data.status === "avgprecipitation"
                                ? data.august
                                  ? data.august
                                  : "-"
                                : data.august
                                ? data.august
                                : "-"}
                            </TableCell>
                            <TableCell
                              className={
                                data.september === "Open"
                                  ? "green-text"
                                  : data.september === "Closed"
                                  ? "red-text"
                                  : "black-text"
                              }
                            >
                              {data.status === "avg. temp (f)" ||
                              data.status === "avgprecipitation"
                                ? data.september
                                  ? data.september
                                  : "-"
                                : data.september
                                ? data.september
                                : "-"}
                            </TableCell>
                            <TableCell
                              className={
                                data.october === "Open"
                                  ? "green-text"
                                  : data.october === "Closed"
                                  ? "red-text"
                                  : "black-text"
                              }
                            >
                              {data.status === "avg. temp (f)" ||
                              data.status === "avgprecipitation"
                                ? data.october
                                  ? data.october
                                  : "-"
                                : data.october
                                ? data.october
                                : "-"}
                            </TableCell>
                            <TableCell
                              className={
                                data.november === "Open"
                                  ? "green-text"
                                  : data.november === "Closed"
                                  ? "red-text"
                                  : "black-text"
                              }
                            >
                              {data.status === "avg. temp (f)" ||
                              data.status === "avgprecipitation"
                                ? data.november
                                  ? data.november
                                  : "-"
                                : data.november
                                ? data.november
                                : "-"}
                            </TableCell>
                            <TableCell
                              className={
                                data.december === "Open"
                                  ? "green-text"
                                  : data.december === "Closed"
                                  ? "red-text"
                                  : "black-text"
                              }
                            >
                              {data.status === "avg. temp (f)" ||
                              data.status === "avgprecipitation"
                                ? data.december
                                  ? data.december
                                  : "-"
                                : data.december
                                ? data.december
                                : "-"}
                            </TableCell>
                          </TableRow>
                        </React.Fragment>
                      );
                    });
                  });
                })
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={"5"}
                    style={{ textAlign: "center" }}
                    className="store-no-data"
                  >
                    No records found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
});

export default SeasonalityPdf;
